import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { filter, take, tap } from 'rxjs/operators';

import { AwesomeTooltipV2Component } from './awesome-tooltip-v2.component';

export type PositionTooltipType = 'topCenter' | 'topRight' | 'topLeft' | 'bottomCenter' | 'rightCenter' | 'leftCenter';

@Directive({
  selector: '[appAwesomeTooltipV2]',
  standalone: false,
})
export class AwesomeTooltipV2Directive implements OnInit, OnDestroy {
  @Input() positionTooltip: PositionTooltipType = 'topCenter';
  @Input() customClass: string = '';
  @Input() patientSignerSaved = null;
  @Input() textAlign: 'center' | 'left' = 'center';
  @Input() tooltipText: string = '';
  @Input() actions: boolean = false;
  @Input() insideTable: boolean = false;
  @Input() isEnableTouch: boolean = true;

  private overlayRef: OverlayRef | null = null;
  private touched: boolean = false;
  private readonly scrollHandler = (): void => this.detachTooltip(true);
  private readonly touchHandler = (event: TouchEvent): void => this.onDocumentTouch(event);
  positionsObj: ConnectedPosition;

  constructor(
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private overlay: Overlay,
    private router: Router,
  ) {}

  ngOnInit() {
    window.dispatchEvent(new Event('scroll', { bubbles: true, cancelable: true }));
    window.addEventListener('scroll', this.scrollHandler, true);
    document.addEventListener('touchstart', this.touchHandler, true);
    this.setPositions();
    this.createOverlay();
    this.router.events
      .pipe(
        filter((event): boolean => event instanceof NavigationEnd),
        tap(() => this.detachTooltip(true)),
        take(1),
      )
      .subscribe();
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.scrollHandler, true);
    document.removeEventListener('touchstart', this.touchHandler, true);
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private setPositions(): void {
    if (this.positionTooltip === 'topCenter') {
      this.positionsObj = {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
      };
    }
    if (this.positionTooltip === 'topRight') {
      this.positionsObj = {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
      };
    }
    if (this.positionTooltip === 'topLeft') {
      this.positionsObj = {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
      };
    }
    if (this.positionTooltip === 'bottomCenter') {
      this.positionsObj = {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
      };
    }
    if (this.positionTooltip === 'rightCenter') {
      this.positionsObj = {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
      };
    }
    if (this.positionTooltip === 'leftCenter') {
      this.positionsObj = {
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center',
      };
    }
  }

  @HostListener('touchstart', ['$event'])
  onMobileTouch(): void {
    if (this.isEnableTouch) {
      this.touched = true;
      this.toggleTooltip();
    }
  }

  @HostListener('mouseenter')
  show(): void {
    if (!this.touched) {
      this.attachTooltip();
    }
  }

  @HostListener('mouseleave')
  hide(): void {
    if (!this.touched) {
      this.overlayRef?.detach();
    }
  }

  @HostListener('click', ['$event'])
  onClick(): void {
    this.detachTooltip(true);
  }

  private toggleTooltip(): void {
    if (this.overlayRef?.hasAttached()) {
      this.detachTooltip(true);
    } else {
      this.attachTooltip();
    }
  }

  private attachTooltip(): void {
    if (this.overlayRef?.hasAttached()) return;

    const component = AwesomeTooltipV2Component;
    const tooltipPortal = new ComponentPortal(component);
    const tooltipRef = this.overlayRef?.attach(tooltipPortal);
    if (!tooltipRef) return;

    tooltipRef.instance.customClass = this.customClass;
    tooltipRef.instance.positionTooltip = this.positionTooltip;
    tooltipRef.instance.patientSignerSaved = this.patientSignerSaved;
    tooltipRef.instance.textAlign = this.textAlign;
    tooltipRef.instance.actions = this.actions;
    tooltipRef.instance.insideTable = this.insideTable;
    tooltipRef.instance.tooltipText = this.tooltipText;
    tooltipRef.changeDetectorRef.detectChanges();
  }

  private onDocumentTouch(event: TouchEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.detachTooltip(true);
    }
  }

  private detachTooltip(resetTouchState: boolean = false): void {
    this.overlayRef?.detach();
    if (resetTouchState && this.isEnableTouch) {
      this.touched = false;
    }
  }

  private createOverlay(): void {
    if (this.overlayRef) return;

    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([this.positionsObj]);

    this.overlayRef = this.overlay.create({ positionStrategy });
  }
}
